/**
 * Keeps a single decimal separator while the user is typing.
 * A comma and a point are both treated as the decimal separator.
 * When both are present, the last one is the decimal separator and the other is a thousands separator.
 */
export function normalizeDecimalTyping(value: string): string {
    const compact = value.replace(/[\s\u00A0\u202F]/g, '');
    if (compact === '' || compact === '-') {
        return compact;
    }

    const negative = compact.startsWith('-');
    const body = negative ? compact.slice(1) : compact;
    const sign = negative ? '-' : '';
    const hasComma = body.includes(',');
    const hasDot = body.includes('.');

    if (hasComma && hasDot) {
        const lastSep = Math.max(body.lastIndexOf(','), body.lastIndexOf('.'));
        const fracDigits = body.slice(lastSep + 1).replace(/[^\d]/g, '');
        if (fracDigits === '') {
            return normalizeDecimalTyping(sign + body.slice(0, lastSep));
        }

        const intDigits = body.slice(0, lastSep).replace(/[^\d]/g, '');
        return `${sign}${intDigits || '0'}.${fracDigits}`;
    }

    const sepIndex = hasComma ? body.indexOf(',') : body.indexOf('.');
    if (sepIndex === -1) {
        return sign + body.replace(/[^\d]/g, '');
    }

    const intDigits = body.slice(0, sepIndex).replace(/[^\d]/g, '');
    const fracDigits = body.slice(sepIndex + 1).replace(/[^\d]/g, '');
    if (fracDigits === '' && (body.endsWith(',') || body.endsWith('.'))) {
        return `${sign}${intDigits}.`;
    }

    if (fracDigits === '') {
        return `${sign}${intDigits}`;
    }

    return `${sign}${intDigits || '0'}.${fracDigits}`;
}

/**
 * Parses a price or other decimal typed with either "," or ".".
 * Incomplete values ("", "-", ".") return null.
 */
export function parseDecimalInput(value: unknown): number | null {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null;
    }

    if (typeof value !== 'string') {
        return null;
    }

    const normalized = normalizeDecimalTyping(value);
    const numeric = normalized.endsWith('.') ? normalized.slice(0, -1) : normalized;
    if (numeric === '' || numeric === '-' || numeric === '.' || numeric === '-.') {
        return null;
    }

    const parsed = Number(numeric);
    return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Rounds a given number or numeric string to a specified number of decimal places.
 *
 * @param {number | string} [value] - The value to be rounded. Can be a number or a numeric string.
 * @param {number} [decimals=2] - The number of decimal places to round the value to. Defaults to 2.
 * @return {number} The rounded value.
 */
export function roundTo(value?: number | string, decimals: number = 2): number {
    value = toNumber(value);

    const factor = Math.pow(10, decimals);
    return Math.round((value + Number.EPSILON) * factor) / factor;
}

/**
 * Converts a given value to a number. If the value is undefined, null, or cannot be converted,
 * the function returns 0.
 *
 * @param {number|string} [value] - The value to be converted, which can be either a number or a string.
 * @return {number} - The resulting number after conversion. Returns 0 if the value is invalid or cannot be converted.
 */
export function toNumber(value?: number | string): number {
    return parseDecimalInput(value) ?? 0;
}

/**
 * Converts a value to a number while preserving null for empty or invalid values.
 *
 * @param {unknown} value - The value to convert.
 * @return {number | null} Parsed number or null when empty/invalid.
 */
export function toNumberOrNull(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    if (typeof value === 'number' || typeof value === 'string') {
        return parseDecimalInput(value);
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
}
